import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Download, File, Users, Calendar, AlertCircle } from 'lucide-react';
import { fileService } from '../services/fileService';

function SharedFiles() {
  const { user } = useAuth();
  const [sharedFiles, setSharedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSharedFiles();
  }, []);

  const loadSharedFiles = async () => {
    try {
      setLoading(true);
      const files = await fileService.getSharedFiles();
      setSharedFiles(files);
      setError('');
    } catch (err) {
      setError('Failed to load shared files: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      await fileService.downloadFile(file.id, file.original_filename);
    } catch (err) {
      setError('Failed to download file: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-virgo-teal-400">Loading shared files...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-virgo-gold-400">Files Shared With Me</h1>
        <p className="text-virgo-teal-300 mt-1">View and download files that others have shared with you</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {sharedFiles.length === 0 ? (
        <div className="bg-virgo-navy-700 rounded-lg shadow p-12 text-center">
          <Users className="mx-auto h-16 w-16 text-virgo-teal-500 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No shared files</h3>
          <p className="text-virgo-teal-400">
            Files that others share with you will appear here
          </p>
        </div>
      ) : (
        <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-virgo-navy-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Shared Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-virgo-navy-700 divide-y divide-gray-200">
              {sharedFiles.map((file) => (
                <tr key={file.id} className="hover:bg-virgo-navy-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <File className="w-5 h-5 text-virgo-teal-500 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-white">
                          {file.original_filename}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {formatFileSize(file.file_size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {file.mime_type || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {formatDate(file.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDownload(file)}
                      className="text-virgo-teal-400 hover:text-blue-900 flex items-center gap-1 ml-auto"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="bg-virgo-navy-600 px-6 py-4 border-t border-virgo-teal-800">
            <p className="text-sm text-virgo-teal-300">
              Showing {sharedFiles.length} shared file{sharedFiles.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SharedFiles;
